/* ============================================================================
   SUTRAYAN — PREMIUM ELEVATION v3 · MOTION (progressive enhancement)
   OWNED BY: MOTION-CORE (implementer A). Deferred, no dependencies.

   The PRIMARY reveal mechanism is the inline <head> bootstrap in
   layout/theme.liquid: it adds `.sy-motion` ONLY after proving that either CSS
   scroll-driven animation (`animation-timeline: view()`) or IntersectionObserver
   exists, then either lets CSS handle reveals or wires an IntersectionObserver.

   This module is SUPPLEMENTARY and REVEAL-ONLY (it can add `.is-revealed` /
   `.is-loaded`, never hide). If it fails to load, content is already visible and
   the inline bootstrap has already handled the initial reveal. It provides:
     1) Progressive image loading  → adds `.is-loaded` to `.sy-img` images.
     2) A public rescan API        → window.SutrayanMotion.scan(root) for content
                                       injected after first paint (AJAX pagination,
                                       Section Rendering swaps, quick-add, etc).
   ============================================================================ */
(function () {
  'use strict';

  var doc = document;
  var mq = window.matchMedia;
  var reduce = !!(mq && mq('(prefers-reduced-motion: reduce)').matches);

  /* Self-inclusive query: matches the root node ITSELF plus its descendants.
     A plain querySelectorAll ignores the root, so a directly-appended matching
     element (e.g. `<img class="sy-img">` or a bare `.sy-reveal`) would be missed
     by the auto-scan and stay blurred/hidden. */
  function queryAll(scope, selector) {
    var list = [];
    if (!scope) return list;
    try {
      if (scope.nodeType === 1 && scope.matches && scope.matches(selector)) list.push(scope);
      if (scope.querySelectorAll) {
        var nl = scope.querySelectorAll(selector);
        for (var i = 0; i < nl.length; i++) list.push(nl[i]);
      }
    } catch (e) { /* invalid scope/selector — return what we have */ }
    return list;
  }

  /* ---------------------------------------------------------------------------
     1) Progressive image loading — safe regardless of motion support.
     CSS keeps images visible by default; only under `.sy-motion` are un-loaded
     `.sy-img` images faded/blurred, so adding `.is-loaded` reveals them. On
     `error` we ALSO mark loaded so a broken image never stays blurred/hidden.
  --------------------------------------------------------------------------- */
  function markLoaded(img) {
    img.classList.add('is-loaded');
  }

  function wireImage(img) {
    if (!img || img.getAttribute('data-sy-img') === 'done') return;
    img.setAttribute('data-sy-img', 'done');
    // If the image is already settled (loaded OR already errored, e.g. a 404 that
    // fired before we wired it), resolve immediately. `complete` is true in both
    // cases; a late-attached 'error' listener would never fire, so relying on it
    // alone would leave a broken image permanently blurred/hidden under .sy-motion.
    if (img.complete) {
      markLoaded(img);
      return;
    }
    img.addEventListener('load', function () { markLoaded(img); }, { once: true });
    img.addEventListener('error', function () { markLoaded(img); }, { once: true });
  }

  function wireImages(root) {
    var imgs = queryAll(root || doc, '.sy-img img, img.sy-img');
    for (var i = 0; i < imgs.length; i++) wireImage(imgs[i]);
  }

  /* ---------------------------------------------------------------------------
     2) Reveal rescan for LATE content only.
     The inline bootstrap already observes every `.sy-reveal` present at
     DOMContentLoaded. This handles nodes injected afterwards. It is a no-op
     when the browser uses the CSS scroll-driven path (view()) or reduced-motion.
  --------------------------------------------------------------------------- */
  var supportsView = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline', 'view()'));
  var hasIO = ('IntersectionObserver' in window);
  var lateObserver = null;

  function getLateObserver() {
    if (lateObserver) return lateObserver;
    lateObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          lateObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    return lateObserver;
  }

  function scanReveals(root) {
    // Only meaningful when hiding is active (.sy-motion) AND the transition/IO
    // path is in use (view() path is fully CSS-driven and self-revealing).
    if (reduce || !hasIO) return; // hotfix 2026-07-03: never trust supportsView — runtime can no-op while CSS.supports is true
    if (!doc.documentElement.classList.contains('sy-motion')) return;
    var nodes = queryAll(root || doc, '.sy-reveal:not(.is-revealed):not([data-sy-obs])');
    if (!nodes.length) return;
    var obs = getLateObserver();
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].setAttribute('data-sy-obs', '');
      obs.observe(nodes[i]);
    }
  }

  function scan(root) {
    wireImages(root);
    scanReveals(root);
  }

  /* ---- Public API (merge, never clobber a pre-existing namespace) ---------- */
  window.SutrayanMotion = window.SutrayanMotion || {};
  window.SutrayanMotion.scan = scan;

  /* ---------------------------------------------------------------------------
     3) Automatic late-content handling (root-cause safety net).
     Content injected after first paint (quick-add, AJAX pagination, Section
     Rendering swaps) is NOT observed by the inline bootstrap. Rather than rely
     on every caller remembering window.SutrayanMotion.scan(), a MutationObserver
     auto-processes added subtrees so late `.sy-reveal` never stays hidden and
     late `.sy-img` never stays blurred. Reveal-only: it can never hide content.
     No-op when the browser uses the CSS scroll-driven path (those nodes get
     animation-timeline:view() for free) or under reduced-motion (no .sy-motion).
  --------------------------------------------------------------------------- */
  function startAutoScan() {
    if (!('MutationObserver' in window)) return;
    var scheduled = false;
    var pending = [];
    function flush() {
      scheduled = false;
      var batch = pending;
      pending = [];
      for (var i = 0; i < batch.length; i++) scan(batch[i]);
    }
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) pending.push(added[j]);
        }
      }
      if (pending.length && !scheduled) {
        scheduled = true;
        // Call with `window` as receiver — a bare `(window.requestAnimationFrame
        // || window.setTimeout)(flush,0)` loses `this` and throws "Illegal
        // invocation" in browsers that require rAF bound to window.
        if (window.requestAnimationFrame) window.requestAnimationFrame(flush);
        else window.setTimeout(flush, 0);
      }
    });
    try {
      mo.observe(doc.body || doc.documentElement, { childList: true, subtree: true });
    } catch (e) { /* fail silent — content already visible by default */ }
  }

  /* ---- Init ---------------------------------------------------------------- */
  function init() {
    // Wire images present at load. Do NOT re-observe initial reveals here — the
    // inline bootstrap already owns them (avoids double observation).
    wireImages(doc);
    startAutoScan();
  }

  if (doc.readyState !== 'loading') {
    init();
  } else {
    doc.addEventListener('DOMContentLoaded', init, { once: true });
  }
})();

/*
 * PSI-A11 (2026-08-13): vendor iframe accessible name.
 *
 * The Google & YouTube app injects `#merchantwidgetiframe` with no `title`,
 * which is the only remaining Accessibility failure on both the homepage and
 * the PDP (axe `frame-title`: screen-reader users get "iframe" with no
 * indication of what it contains). We do not control the app's markup, so the
 * title is applied after it lands.
 *
 * This is a patch on a symptom, not a cure — the same embed also costs ~250 KiB
 * of gstatic JS, ~621ms of CPU and a third-party cookie, so if the store-reviews
 * badge is not earning its keep the better fix is to disable the app entirely
 * (owner action, Shopify admin). Kept deliberately narrow: one known id, set
 * once, no polling beyond the observer, and it never touches iframe content.
 */
(function () {
  var TITLE = 'Google customer reviews for Sutrayan';

  // Returns true once the iframe EXISTS — whether or not it needed labelling.
  // Returning false when it already carries its own title would leave the
  // observer running on every DOM mutation for the full backstop window.
  function label(root) {
    var f = (root || document).querySelector('#merchantwidgetiframe');
    if (!f) return false;
    if (!f.getAttribute('title')) f.setAttribute('title', TITLE);
    return true;
  }

  if (label(document)) return;

  // The widget mounts asynchronously; stop watching as soon as it is labelled
  // so we never leave a live observer running on the page.
  var obs = new MutationObserver(function () {
    if (label(document)) obs.disconnect();
  });

  function start() {
    // Re-check either side of observe(): the widget can land between the first
    // check and this call (body exists, DOMContentLoaded has not fired), and a
    // MutationObserver only reports FUTURE mutations — so without these the
    // already-present iframe would never be labelled.
    if (label(document)) return;
    obs.observe(document.body, { childList: true, subtree: true });
    if (label(document)) {
      obs.disconnect();
      return;
    }
    // Backstop: never observe forever if the app is uninstalled or blocked.
    setTimeout(function () {
      obs.disconnect();
    }, 20000);
  }

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
